import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Breadcrumbs,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  Link,
} from '@mui/material';
import {
  Folder as FolderIcon,
  CreateNewFolder as CreateNewFolderIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { MediaFolder, MediaFile, MediaImage } from '../../models/media';
import { 
  createFolder, 
  deleteFolder, 
  updateFolder, 
  getFolderContents,
  deleteFile,
  bulkDeleteFiles,
  getFolderPath,
} from '../../services/media';

interface FolderBrowserProps {
  onSelectFile?: (file: MediaFile | MediaImage) => void;
  onUploadClick?: () => void;
  selectionMode?: boolean;
  fileTypeFilter?: 'all' | 'images' | 'documents';
  currentFolderId?: number | null | undefined;
  onFolderChange?: (folderId: number | null | undefined) => void;
}

const FolderBrowser: React.FC<FolderBrowserProps> = ({ 
  onSelectFile,
  onUploadClick,
  selectionMode = false,
  fileTypeFilter = 'all',
  currentFolderId,
  onFolderChange,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [folders, setFolders] = useState<MediaFolder[]>([]);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [images, setImages] = useState<MediaImage[]>([]);
  const [currentFolder, setCurrentFolder] = useState<MediaFolder | null>(null);
  const [folderPath, setFolderPath] = useState<MediaFolder[]>([]);
  
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  
  const [renameDialogOpen, setRenameDialogOpen] = useState<boolean>(false);
  const [renameItemType, setRenameItemType] = useState<'folder' | 'file'>('folder');
  const [renameItemId, setRenameItemId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [deleteType, setDeleteType] = useState<'folder' | 'file' | 'bulk'>('folder');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<(MediaFile | MediaImage)[]>([]);
  
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    type: 'folder' | 'file';
    item: MediaFolder | MediaFile | MediaImage;
  } | null>(null);
  
  // Load folder contents using callback to avoid dependency issues
  const loadFolderContents = useCallback(async (folderId?: number | null | undefined) => {
    setLoading(true);
    try {
      const contents = await getFolderContents(folderId);
      setFolders(contents.folders || []);
      
      if (fileTypeFilter === 'all') {
        setFiles(contents.files || []);
        setImages(contents.images || []);
      } else if (fileTypeFilter === 'images') {
        setFiles([]);
        setImages(contents.images || []);
      } else if (fileTypeFilter === 'documents') {
        setFiles(contents.files || []);
        setImages([]);
      }
      
      // Update folder path
      if (folderId === undefined || folderId === null) {
        // Reset to root
        setCurrentFolder(null);
        setFolderPath([]);
        if (onFolderChange) {
          onFolderChange(null);
        }
      } else {
        // Get the complete folder path
        try {
          const folderPath = await getFolderPath(folderId);
          setFolderPath(folderPath);
          
          // Set current folder to the last one in the path
          const currentFolder = folderPath.length > 0 ? folderPath[folderPath.length - 1] : null;
          setCurrentFolder(currentFolder);
          
          if (onFolderChange) {
            onFolderChange(folderId);
          }
        } catch (pathError) {
          console.error('Error getting folder path:', pathError);
          
          // Fallback: use the folder from contents if available
          const folder = contents.folders.find(f => f.id === folderId);
          if (folder) {
            setCurrentFolder(folder);
            
            // Get current folderPath but don't create a dependency on it
            setFolderPath(prevPath => {
              const newPath = [...prevPath];
              // If already in path, truncate to that point
              const existingIndex = newPath.findIndex(f => f.id === folder.id);
              if (existingIndex >= 0) {
                newPath.splice(existingIndex + 1);
              } else {
                newPath.push(folder);
              }
              return newPath;
            });
            
            if (onFolderChange) {
              onFolderChange(folder.id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading folder contents:', error);
    } finally {
      setLoading(false);
    }
  }, [onFolderChange, fileTypeFilter]); // Removed folderPath from dependencies
  
  // Load folder contents only when necessary dependencies change
  useEffect(() => {
    loadFolderContents(currentFolderId);
  }, [currentFolderId, fileTypeFilter, loadFolderContents]);
  
  const handleOpenFolder = (folder: MediaFolder) => {
    if (onFolderChange) {
      onFolderChange(folder.id);
    } else {
      loadFolderContents(folder.id);
    }
  };
  
  const handleNavigateUp = () => {
    if (folderPath.length === 0) {
      // Already at root
      return;
    }
    
    if (folderPath.length === 1) {
      // Go to root
      if (onFolderChange) {
        onFolderChange(null);
      } else {
        loadFolderContents();
      }
    } else {
      // Go up one level
      const parentFolder = folderPath[folderPath.length - 2];
      if (onFolderChange) {
        onFolderChange(parentFolder.id);
      } else {
        loadFolderContents(parentFolder.id);
      }
    }
  };
  
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setLoading(true);
    try {
      const parentId = currentFolder?.id;
      const newFolder = await createFolder(newFolderName, parentId);
      setFolders([...folders, newFolder]);
      setNewFolderName('');
      setCreateFolderDialogOpen(false);
    } catch (error) {
      console.error('Error creating folder:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteItem = async () => {
    setLoading(true);
    try {
      if (deleteType === 'folder' && deleteId) {
        await deleteFolder(deleteId);
        setFolders(folders.filter(f => f.id !== deleteId));
      } else if (deleteType === 'file' && deleteId) {
        await deleteFile(deleteId);
        setFiles(files.filter(f => f.id !== deleteId));
        setImages(images.filter(i => i.id !== deleteId));
      } else if (deleteType === 'bulk' && selectedFiles.length > 0) {
        const fileIds = selectedFiles.map(file => file.id);
        await bulkDeleteFiles(fileIds);
        setFiles(files.filter(f => !fileIds.includes(f.id)));
        setImages(images.filter(i => !fileIds.includes(i.id)));
        setSelectedFiles([]);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    } finally {
      setConfirmDeleteOpen(false);
      setDeleteId(null);
      setLoading(false);
    }
  };
  
  const handleRenameItem = async () => {
    if (!renameValue.trim() || renameItemId === null) return;
    
    setLoading(true);
    try {
      if (renameItemType === 'folder') {
        const updatedFolder = await updateFolder(renameItemId, renameValue);
        setFolders(folders.map(f => f.id === renameItemId ? updatedFolder : f));
      } else {
        // File renaming handled in parent component
      }
    } catch (error) {
      console.error('Error renaming item:', error);
    } finally {
      setRenameDialogOpen(false);
      setRenameItemId(null);
      setRenameValue('');
      setLoading(false);
    }
  };
  
  const handleContextMenu = (
    event: React.MouseEvent,
    type: 'folder' | 'file',
    item: MediaFolder | MediaFile | MediaImage
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      type,
      item,
    });
  };
  
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };
  
  const handleFileClick = (file: MediaFile | MediaImage) => {
    if (selectionMode && onSelectFile) {
      onSelectFile(file);
    } else {
      // Toggle selection
      const isSelected = selectedFiles.some(f => f.id === file.id);
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
      } else {
        setSelectedFiles([...selectedFiles, file]);
      }
    }
  };
  
  const openRenameDialog = (type: 'folder' | 'file', id: number, currentName: string) => {
    setRenameItemType(type);
    setRenameItemId(id);
    setRenameValue(currentName);
    setRenameDialogOpen(true);
    handleCloseContextMenu();
  };
  
  const openDeleteDialog = (type: 'folder' | 'file', id: number) => {
    setDeleteType(type);
    setDeleteId(id);
    setConfirmDeleteOpen(true);
    handleCloseContextMenu();
  };
  
  const renderBreadcrumbs = () => {
    return (
      <Breadcrumbs 
        aria-label="folder navigation breadcrumb"
        separator="/"
        sx={{ 
          p: 1, 
          borderRadius: 1,
          bgcolor: 'background.paper', 
          '& .MuiBreadcrumbs-ol': { 
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 6,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'action.hover',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'divider',
              borderRadius: 3,
            },
          },
          boxShadow: 1,
          overflowX: 'auto',
          maxWidth: '100%'
        }}
      >
        <Link 
          component="button"
          underline="hover" 
          color="inherit" 
          onClick={() => {
            if (onFolderChange) {
              onFolderChange(null);
            } else {
              loadFolderContents();
            }
          }}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            fontWeight: 'medium',
            color: 'primary.main'
          }}
        >
          <FolderIcon sx={{ mr: 0.5, fontSize: '0.9rem' }} />
          Root
        </Link>
        {folderPath.map((folder, index) => (
          <Link
            key={folder.id}
            component="button"
            underline="hover"
            color={index === folderPath.length - 1 ? "text.primary" : "inherit"}
            onClick={() => {
              if (onFolderChange) {
                onFolderChange(folder.id);
              } else {
                loadFolderContents(folder.id);
              }
            }}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              maxWidth: { xs: '100px', sm: '150px', md: '200px' },
              ...(index === folderPath.length - 1 ? { 
                fontWeight: 'bold',
                color: 'text.primary'
              } : {}),
              '&:hover': {
                bgcolor: 'action.hover',
                borderRadius: 1,
                px: 0.5
              }
            }}
          >
            <FolderIcon sx={{ mr: 0.5, fontSize: '0.9rem', color: 'warning.main' }} />
            <Typography noWrap title={folder.name}>
              {folder.name}
            </Typography>
          </Link>
        ))}
      </Breadcrumbs>
    );
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      {loading && <LinearProgress />}
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {folderPath.length > 0 && (
          <IconButton onClick={handleNavigateUp}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {renderBreadcrumbs()}
        </Box>
        <Button 
          startIcon={<CreateNewFolderIcon />}
          onClick={() => setCreateFolderDialogOpen(true)}
          sx={{ mr: 1 }}
        >
          New Folder
        </Button>
        {onUploadClick && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={onUploadClick}
          >
            Upload Files
          </Button>
        )}
      </Box>
      
      {selectedFiles.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {selectedFiles.length} items selected
          </Typography>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={() => {
              setDeleteType('bulk');
              setConfirmDeleteOpen(true);
            }}
          >
            Delete Selected
          </Button>
        </Box>
      )}
      
      <Grid container spacing={2}>
        {/* Folders */}
        {folders.map(folder => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={folder.id}>
            <Card 
              onClick={() => handleOpenFolder(folder)}
              onContextMenu={(e) => handleContextMenu(e, 'folder', folder)}
              sx={{ 
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <FolderIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="subtitle1" noWrap>{folder.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {folder.child_count} folders, {folder.file_count} files
                  </Typography>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRenameDialog('folder', folder.id, folder.name);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog('folder', folder.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
        
        {/* Files */}
        {files.map(file => {
          const isSelected = selectedFiles.some(f => f.id === file.id);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
              <Card 
                onClick={() => handleFileClick(file)}
                onContextMenu={(e) => handleContextMenu(e, 'file', file)}
                sx={{ 
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : 'none',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                  <DocumentIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography variant="subtitle1" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {file.original_filename} ({file.file_size_display})
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog('file', file.id);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
        
        {/* Images */}
        {images.map(image => {
          const isSelected = selectedFiles.some(f => f.id === image.id);
          const thumbnailUrl = image.thumbnail_url?.small || image.thumbnail_url?.original || '#';
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
              <Card 
                onClick={() => handleFileClick(image)}
                onContextMenu={(e) => handleContextMenu(e, 'file', image)}
                sx={{ 
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : 'none',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Box sx={{ 
                  height: 140, 
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'background.default'
                }}>
                  {thumbnailUrl !== '#' ? (
                    <img 
                      src={thumbnailUrl} 
                      alt={image.name}
                      style={{ 
                        maxHeight: '100%', 
                        maxWidth: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                  )}
                </Box>
                <CardContent sx={{ pt: 1, pb: 1 }}>
                  <Typography variant="subtitle2" noWrap title={image.name}>
                    {image.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {image.file_size_display}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog('file', image.id);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {/* Empty state */}
      {folders.length === 0 && files.length === 0 && images.length === 0 && !loading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 6,
          opacity: 0.7
        }}>
          <FolderIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6">This folder is empty</Typography>
          <Typography variant="body2">
            Upload files or create folders to organize your media
          </Typography>
        </Box>
      )}
      
      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.type === 'folder' && (
          <>
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                openRenameDialog('folder', (contextMenu.item as MediaFolder).id, (contextMenu.item as MediaFolder).name);
              }
            }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Rename
            </MenuItem>
            <MenuItem onClick={() => {
              if (contextMenu?.item) {
                openDeleteDialog('folder', (contextMenu.item as MediaFolder).id);
              }
            }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}
        
        {contextMenu?.type === 'file' && (
          <MenuItem onClick={() => {
            if (contextMenu?.item) {
              openDeleteDialog('file', (contextMenu.item as MediaFile).id);
            }
          }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>
      
      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialogOpen} onClose={() => setCreateFolderDialogOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>
      
      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>
          Rename {renameItemType === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={renameItemType === 'folder' ? 'Folder Name' : 'File Name'}
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenameItem} disabled={!renameValue.trim()}>Rename</Button>
        </DialogActions>
      </Dialog>
      
      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteType === 'folder' && 'Are you sure you want to delete this folder and all its contents?'}
            {deleteType === 'file' && 'Are you sure you want to delete this file?'}
            {deleteType === 'bulk' && `Are you sure you want to delete ${selectedFiles.length} selected item(s)?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteItem} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FolderBrowser;
